import apiCall from './api';

/**
 * Service pour les statistiques et dashboard
 * ✅ Version utilisant uniquement les données de la base de données
 */
export const statsService = {

  /**
   * ✅ Récupérer les statistiques du jour depuis la base de données
   */
  getTodayStats: async () => {
    try {
      console.log('📊 Récupération des statistiques du jour depuis la base...');
      
      const response = await apiCall.get('/manager/stats/today');
      
      if (response.success) {
        console.log('✅ Stats du jour récupérées depuis la base:', response.data);
        return response;
      } else {
        console.warn('⚠️ Erreur récupération stats:', response.message);
        return {
          success: false,
          data: null,
          message: response.message || 'Statistiques non disponibles'
        };
      }
    } catch (error) {
      console.error('❌ Erreur statistiques du jour:', error);
      return {
        success: false,
        data: null,
        message: 'Erreur de connexion au serveur'
      };
    }
  },

  /**
   * ✅ Récupérer le top des produits depuis la base de données
   */
  getTopProducts: async (limit = 5) => {
    try {
      console.log('🏆 Récupération du top produits depuis la base...', limit);
      
      const response = await apiCall.get('/manager/stats/top-products', { limit });
      
      if (response.success) {
        console.log('✅ Top produits récupérés depuis la base:', response.data?.length || 0);
        return response;
      } else {
        console.warn('⚠️ Erreur récupération top produits:', response.message);
        return {
          success: false,
          data: [],
          message: response.message || 'Top produits non disponible'
        };
      }
    } catch (error) {
      console.error('❌ Erreur top produits:', error);
      return {
        success: false,
        data: [],
        message: 'Erreur de connexion au serveur'
      };
    }
  },

  /**
   * ✅ Récupérer le dashboard complet depuis la base de données
   */
  getDashboard: async () => {
    try {
      console.log('📊 Récupération dashboard complet depuis la base...');
      
      const response = await apiCall.get('/manager/dashboard');
      
      if (response.success) {
        console.log('✅ Dashboard complet récupéré depuis la base:', response.data);
        return response;
      } else {
        console.warn('⚠️ Dashboard non disponible, assemblage manuel...');
        
        // Assembler les données depuis les endpoints séparés
        const [todayStats, topProducts] = await Promise.allSettled([
          statsService.getTodayStats(),
          statsService.getTopProducts(5)
        ]);

        const dashboardData = {
          todayStats: todayStats.status === 'fulfilled' && todayStats.value.success ? 
            todayStats.value.data : null,
          topProducts: topProducts.status === 'fulfilled' && topProducts.value.success ? 
            topProducts.value.data : [],
          stockAlerts: [],
          creditSales: [],
          teamActivity: []
        };

        console.log('✅ Dashboard assemblé depuis les endpoints séparés');
        return {
          
          success: true,
          data: dashboardData,
          message: 'Dashboard assemblé depuis les données disponibles'
        };
      }
    } catch (error) {
      console.error('❌ Erreur dashboard complet:', error);
      return {
        success: false,
        data: null,
        message: 'Erreur de connexion au serveur'
      };
    }
  },

  /**
   * ✅ Récupérer le montant des crédits en cours
   */
  getCreditBalance: async () => {
    try {
      console.log('💳 Récupération du montant des crédits depuis la base...');
      
      const response = await apiCall.get('/manager/stats/credit-balance');
      
      if (response.success) {
        console.log('✅ Montant crédits récupéré:', response.data);
        return response;
      } else {
        console.warn('⚠️ Erreur récupération crédits:', response.message);
        return {
          success: false,
          data: 0,
          message: response.message || 'Montant crédits non disponible'
        };
      }
    } catch (error) {
      console.error('❌ Erreur montant crédits:', error);
      return {
        success: false,
        data: 0,
        message: 'Erreur de connexion au serveur'
      };
    }
  },

  /**
   * ✅ Récupérer les alertes de stock depuis la base de données
   */
  getStockAlerts: async () => {
    try {
      console.log('⚠️ Récupération des alertes de stock depuis la base...');
      
      const response = await apiCall.get('/manager/stock/alerts');
      
      if (response.success) {
        console.log('✅ Alertes stock récupérées:', response.data?.length || 0);
        return response;
      } else {
        console.warn('⚠️ Erreur récupération alertes stock:', response.message);
        return {
          success: false,
          data: [],
          message: response.message || 'Alertes stock non disponibles'
        };
      }
    } catch (error) {
      console.error('❌ Erreur alertes stock:', error);
      return {
        success: false,
        data: [],
        message: 'Erreur de connexion au serveur'
      };
    }
  },

  /**
   * ✅ Récupérer l'activité de l'équipe depuis la base de données
   */
  getTeamActivity: async () => {
    try {
      console.log('👥 Récupération de l\'activité de l\'équipe depuis la base...');
      
      const response = await apiCall.get('/manager/team/activity');
      
      if (response.success) {
        console.log('✅ Activité équipe récupérée:', response.data?.length || 0);
        return response;
      } else {
        console.warn('⚠️ Erreur récupération activité équipe:', response.message);
        return {
          success: false,
          data: [],
          message: response.message || 'Activité équipe non disponible'
        };
      }
    } catch (error) {
      console.error('❌ Erreur activité équipe:', error);
      return {
        success: false,
        data: [],
        message: 'Erreur de connexion au serveur'
      };
    }
  },

  /**
   * ✅ NOUVEAU : Performance de l'équipe (stats par vendeur calculées côté serveur)
   * Remplace saleService.getSales() + calculs frontend
   */
  getTeamPerformance: async () => {
    try {
      console.log('👥 Récupération performance équipe depuis le serveur...');
      const response = await apiCall.get('/manager/team/performance');
      
      if (response.success) {
        console.log('✅ Performance équipe récupérée:', response.data);
        return response;
      } else {
        return { success: false, data: null, message: response.message };
      }
    } catch (error) {
      console.error('❌ Erreur performance équipe:', error);
      return { success: false, data: null, message: 'Erreur de connexion' };
    }
  }
};

export default statsService;